import type { ChannexStatus, ChannexSyncMap, OtaConnection } from "@/types";
import { apiClient } from "./client";

export interface ServiceSummary {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  owner_email: string | null;
  owner_name: string | null;
  counts: Record<string, number>;
}

export interface CollectionPage {
  total: number;
  items: Record<string, unknown>[];
}

export interface QueuedProperty {
  id: string;
  name: string;
  service_id: string;
  service_name: string;
  owner_email: string | null;
  status: ChannexStatus;
  support_email: string;
  support_phone: string;
  updated_at: string;
  ota_connections: OtaConnection[];
}

export interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  is_approved: boolean;
  created_at: string;
}

/** 전 테넌트 Channex 동기화 상태 — 나쁜 것부터 내려온다. */
export interface ChannexSyncRow {
  property_id: string;
  name: string;
  service_id: string;
  service_name: string;
  status: ChannexStatus;
  channex_property_id: string | null;
  sync: ChannexSyncMap;
}

export interface ChannexSyncHealth {
  stale_after_minutes: number;
  alerts_enabled: boolean;
  alert_after_failures: number;
  rows: ChannexSyncRow[];
}

/** 숙소별 LLM 토큰·추정 원가. 비용은 서버가 지금의 단가표로 매번 추정한 값이다. */
export interface LlmUsageRow {
  property_id: string;
  name: string;
  service_name: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  /** 단가가 설정된 모델만 합산한 추정 비용. */
  cost: number;
  /** 단가 미설정이라 비용 합계에서 빠진 호출 수. */
  unpriced_calls: number;
  purposes: Record<string, number>;
  providers: Record<string, number>;
}

export interface LlmUsage {
  days: number;
  since: string;
  currency: string;
  unpriced_models: string[];
  rows: LlmUsageRow[];
}

export interface ImpersonationToken {
  access_token: string;
  token_type: string;
  admin_id: string;
  email: string;
  name: string;
  expires_in: number;
}

export const superadminService = {
  listAdminUsers: async (): Promise<AdminUserRow[]> => {
    const { data } = await apiClient.get<AdminUserRow[]>("/superadmin/admin-users");
    return data;
  },

  setAdminApproval: async (adminId: string, isApproved: boolean): Promise<AdminUserRow> => {
    const { data } = await apiClient.patch<AdminUserRow>(
      `/superadmin/admin-users/${adminId}/approval`,
      { is_approved: isApproved }
    );
    return data;
  },

  impersonateAdmin: async (adminId: string): Promise<ImpersonationToken> => {
    const { data } = await apiClient.post<ImpersonationToken>(
      `/superadmin/admin-users/${adminId}/impersonate`
    );
    return data;
  },

  listServices: async (): Promise<ServiceSummary[]> => {
    const { data } = await apiClient.get<ServiceSummary[]>("/superadmin/services");
    return data;
  },

  browseCollection: async (
    serviceId: string,
    name: string,
    skip: number,
    limit: number
  ): Promise<CollectionPage> => {
    const { data } = await apiClient.get<CollectionPage>(
      `/superadmin/services/${serviceId}/collections/${name}`,
      { params: { skip, limit } }
    );
    return data;
  },

  channexSyncHealth: async (): Promise<ChannexSyncHealth> => {
    const { data } = await apiClient.get<ChannexSyncHealth>("/superadmin/channex/sync");
    return data;
  },

  llmUsage: async (days = 30): Promise<LlmUsage> => {
    const { data } = await apiClient.get<LlmUsage>("/superadmin/llm-usage", { params: { days } });
    return data;
  },

  listPropertyQueue: async (): Promise<QueuedProperty[]> => {
    const { data } = await apiClient.get<QueuedProperty[]>("/superadmin/properties/queue");
    return data;
  },

  approveProperty: async (id: string): Promise<{ channex_status: ChannexStatus }> => {
    const { data } = await apiClient.post<{ channex_status: ChannexStatus }>(
      `/superadmin/properties/${id}/approve`
    );
    return data;
  },

  transitionProperty: async (
    id: string,
    status: "integrating" | "connected"
  ): Promise<{ channex_status: ChannexStatus }> => {
    const { data } = await apiClient.post<{ channex_status: ChannexStatus }>(
      `/superadmin/properties/${id}/status`,
      { status }
    );
    return data;
  },
};
